import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Avatar,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Backdrop,
  CircularProgress,
  Skeleton,
  Grid,
} from "@mui/material";
import { encryptId } from "../../../Components/CryptoComponent";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import WorkIcon from "@mui/icons-material/Work";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import SelectInput from "../../../Components/InputComponents/SelectInput";
import { errorNotify } from "../../../Components/ToastComponent/ToastNotification";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const RecruitmentProcedureMappingList = () => {
  const navigate = useNavigate();

  const [recruitmentProcedureData, setRecruitmentProcedureData] = useState([]);

  const [recruitmentProcedureList, setRecruitmentProcedureList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [professionList, setProfessionList] = useState([]);

  const [filterData, setFilterData] = useState({
    profession: "",
    country: "",
    recruitmentProcedure: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loaderOpen, setLoaderOpen] = useState(true);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    getData();
    setPage(0)
  };

  const handleResetFilterData = () => {
    const resetFilters = {
      profession: "",
      country: "",
      recruitmentProcedure: "",
    };

    const isAlreadyReset = Object.entries(resetFilters).every(
      ([key, value]) => filterData[key] === value
    );

    if (!isAlreadyReset) {
      setFilterData(resetFilters);
      getData(resetFilters);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterData({
      ...filterData,
      [name]: value,
    });
  };

  const getData = (filters = filterData) => {
    const trimmedFilters = {
      RPM_CountryId: filters?.country,
      RPM_ProfessionId: filters?.profession,
      RPM_Procedure: filters?.recruitmentProcedure,
    };

    axiosAPI
      ?.post(`/RecruitmentProcedureMapping/list`, trimmedFilters)
      .then((res) => {
        setRecruitmentProcedureData(res?.data?.data);
        setLoaderOpen(false);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const getProfession = async () => {
    try {
      const res = await axiosAPI.get(`/Profession/active/list`);
      setProfessionList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getCountries = async () => {
    try {
      const res = await axiosAPI.get(`/RecruitmentCountry/list`);
      setCountryList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getRecruitmentProcedures = async () => {
    try {
      const res = await axiosAPI.get(`/RecruitmentProcedure/list`);
      setRecruitmentProcedureList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
    getProfession();
    getCountries();
    getRecruitmentProcedures();
  }, [page, rowsPerPage]);

  const navigatetoAdd = () => {
    navigate(`/RecruitmentMappingProcedure/Add`);
  };

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        width: { xs: "100vw", sm: "100vw", md: "100%" },
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Recruitment Procedure Mapping
        </Typography>
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={navigatetoAdd}
            sx={{ background: "#E75E1F", textTransform: "capitalize" }}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Grid container spacing={2} mb={2}>
        {/* Profession */}
        <Grid item xs={12} sm={4}>
          <SelectInput
            value={filterData?.profession}
            name="profession"
            onChange={handleFilterChange}
            options={professionList?.map((item) => ({
              value: item?.PI_Id,
              label: item?.PI_Name,
            }))}
            placeholder="Select Profession"
            sx={{ width: "100%" }}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} sm={4}>
          <SelectInput
            value={filterData?.country}
            name="country"
            onChange={handleFilterChange}
            options={countryList?.map((item) => ({
              value: item?.RC_Id,
              label: item?.RC_CountryName,
            }))}
            placeholder="Select Country"
            sx={{ width: "100%" }}
          />
        </Grid>

        {/* Recruitment Procedure */}
        <Grid item xs={12} sm={4}>
          <SelectInput
            value={filterData?.recruitmentProcedure}
            name="recruitmentProcedure"
            onChange={handleFilterChange}
            options={recruitmentProcedureList?.map((item) => ({
              value: item?.RP_Id,
              label: item?.RP_Name,
            }))}
            placeholder="Select Recruitment Procedure"
            sx={{ width: "100%" }}
          />
        </Grid>

        {/* Buttons Row - full width */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<Search />}
              onClick={handleApplyFilters}
              sx={{
                textTransform: "capitalize",
                color: "white",
                background: "#006CB8",
              }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={handleResetFilterData}
              sx={{ textTransform: "capitalize" }}
            >
              Reset
            </Button>
          </Box>
        </Grid>
      </Grid>


      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "90vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "80px" }}>#</StyledTableCell>
              <StyledTableCell>Assigned Profession</StyledTableCell>
              <StyledTableCell>Assigned Country</StyledTableCell>
              <StyledTableCell>Recruitment Procedure</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderOpen ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <StyledTableCell key={index}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))
            ) : recruitmentProcedureData &&
              recruitmentProcedureData?.length > 0 ? (
              recruitmentProcedureData
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((c, index) => (
                  <StyledTableRow key={c.id}>
                    <StyledTableCell>
                      {(page + 1) * rowsPerPage - (rowsPerPage - (index + 1))}
                    </StyledTableCell>
                    <StyledTableCell>{c?.Profession_Name}</StyledTableCell>
                    <StyledTableCell>{c?.country_name}</StyledTableCell>
                    <StyledTableCell>
                      <Link
                        to={`/RecruitmentProcedure/View/${encodeURIComponent(
                          encryptId(c?.RPM_Procedure)
                        )}?from=RecruitmentMappingProcedure`}
                      >
                        {c?.Recruitment_Procedure}
                      </Link>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={recruitmentProcedureData?.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default RecruitmentProcedureMappingList;
